import type { CareSeekerShallowLeadFormDataV2 } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroVariant } from "../shared/hero-variants";

import { ServiceLocationTrackedClickEvent } from "@homeinstead/analytics";
import { cn } from "@homeinstead/ui";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { Controller, useFormContext } from "react-hook-form";
import { usePostalCodeAnalytics } from "~www/hooks/usePostalCodeAnalytics";
import ContinueButton from "../shared/ContinueButton";
import { getHeroVariantStyles } from "../shared/hero-variants";
import { Testimony } from "../Testimony";
import { testimonies } from "../testimonies";

const LocationStepV4 = ({
  nextStep,
  className,
  variant = "default",
}: {
  className?: string;
  nextStep: () => void;
  variant?: HeroVariant;
}) => {
  const { register, trigger, watch, control, getFieldState } =
    useFormContext<CareSeekerShallowLeadFormDataV2>();

  const location = watch("location", "");
  const franchiseNumber = watch("organizationNumber", 0);

  const { invalid: invalidInput } = getFieldState("location");

  const { textColorClass, errorColorClass } = getHeroVariantStyles(variant);

  const userPostalCodeValid = !!location.trim() && !invalidInput;

  const trackingEvent = {
    eventName: "tracked_click" as const,
    eventPayloadGenerator: () => {
      return ServiceLocationTrackedClickEvent(
        location.trim() === "" ? undefined : Number(location.trim()),
        Number(franchiseNumber),
        "v4",
      );
    },
  };

  const zipcodeUserSelection: Record<string, string> = {};
  if (location.trim()) {
    zipcodeUserSelection.postalCode = location.trim();
  }
  if (franchiseNumber) {
    zipcodeUserSelection.franchiseNumber = String(franchiseNumber);
  }
  const zipcodeLeadFormStep = {
    formVariant: "v4" as const,
    stepName: "zipcode",
    userSelection: zipcodeUserSelection,
  };

  usePostalCodeAnalytics({
    error: null,
    formVariant: "v4",
    isFetching: false,
    isValid: userPostalCodeValid,
    locationsAvailable: false,
    postalCode: location.trim(),
  });

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4 gap-y-24 xl:flex-row xl:gap-y-4",
        className,
      )}
      data-testid="location-step-v4"
    >
      <div className="flex w-full flex-col gap-4 xl:max-w-[440px]">
        <h1
          className={cn(
            "text-left font-bold font-jakarta",
            "text-xl md:text-1xl",
            textColorClass,
          )}
        >
          Where is care needed?
        </h1>
        <p className={cn("mb-4 text-base", textColorClass)}>
          Please enter the location where care is needed.
        </p>

        <div className="max-w-[440px]">
          <Controller
            control={control}
            name="location"
            render={({ field, fieldState }) => (
              <div className="max-w-[440px]">
                <input
                  type="hidden"
                  value={location}
                  {...register("postalCode")}
                />
                <Input
                  className="peer bg-white text-base text-new-beginnings-base"
                  placeholder="Postal Code"
                  type="text"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger("location");
                  }}
                />
                {fieldState.error && (
                  <FormError.span
                    className={cn("mt-2 max-w-[440px]", errorColorClass)}
                  >
                    {fieldState.error.message}
                  </FormError.span>
                )}
              </div>
            )}
          />
        </div>

        {userPostalCodeValid && (
          <ContinueButton
            isEnabled
            leadFormStep={zipcodeLeadFormStep}
            nextStep={nextStep}
            trackingEvent={trackingEvent}
          />
        )}
      </div>

      <Testimony
        className="mb-auto ml-auto flex max-w-md flex-col gap-4"
        variant={variant}
      >
        {testimonies.step4}
      </Testimony>
    </div>
  );
};

export default LocationStepV4;
